import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kCallbackNotify } from "../../../Admin/kReusableCoreTS/kCallbackNotify";
import { AppRawDone } from "./AppAccountPost";
import { AppChatArchiveApi, AppChatArchiveAsk, IAppChatArchiveCall } from "./AppChatArchive";
import { AppChatArchiveRules, AppChatArchiveWho, AppChatSettingValue } from "./AppChatArchiveRules";
import { AppChatProp, AppChatStateDoc } from "./AppChatState";
import { AppChatSettingsModel, AppChatSettingsRules, AppChatSettingsVerdict } from "./AppChatSettingsRules";
import { IAppLiveClock } from "./AppLiveFeed";

// AppChatSettingsRun - ONE CHAT'S SETTINGS WHILE THE "CHAT SETTINGS" DIALOG IS OPEN: one read of `/chat/settings`, the
// owner's changes, and what is drawn meanwhile (`AppChatSettingsRules` words every state). No DOM, no transport of its
// own (`IAppChatSettingsPort`, `IAppLiveClock`, `IAppChatSettingsScreen`) - the suite drives it by hand.
//
// ⭐ ONE READ FOR ALL FOUR SETTINGS, asked again only for a reason, each with a bound:
//   - a LOST read (no answer, a 5xx, a "try again in N s" refusal): after 2, 4, 8, 8, 8 s (or the service's N), at most
//     `MaxRetries` times. Meanwhile the rows keep the last values read, or their loading look. After the last try the
//     dialog says ONE sentence and offers "Try again" - ⛔ never a row without a value.
//   - `resolving` (the service is reading from WhatsApp): after its `recheckSec`, at most `MaxRechecks` times;
//   - a change awaiting WhatsApp's confirmation: every `ConfirmMs`, `MaxConfirms` times, then slower, then not at all.
// ⭐ A CHANGE IS SHOWN AT ONCE AND SETTLED BY THE SERVICE'S ANSWER (optimistic, with rollback): the answer shows the
//   value -> done (`OnChanged`); the service refused -> the earlier value comes back with the service's sentence beside
//   the control; "pending" or no answer -> the chosen value stays, marked as waiting, until a read settles it either way.
// ⛔ ONE REQUEST IN FLIGHT PER SETTING: a second press on a setting that is saving or waiting does nothing.
// ⛔ A LATE ANSWER IS INERT: a reply carries the generation it was asked in; `Stop` (the dialog closed) ends them all.
// ⛔ Nothing here sends a message (decision 9) - a setting of the chat is not a message, and no sender is reachable.

/** The service, as the dialog needs it. */
export interface IAppChatSettingsPort {
    Read(Who: AppChatArchiveWho, Done: AppRawDone): IAppChatArchiveCall;
    Write(Who: AppChatArchiveWho, Value: AppChatSettingValue, Done: AppRawDone): IAppChatArchiveCall;
}

/** The page's port: `GET /chat/settings` (the key in a header) and `POST /chat/settings {action: "set", setting, value}`. */
export class AppChatSettingsPort implements IAppChatSettingsPort {
    private Api: AppChatArchiveApi = new AppChatArchiveApi();

    public Read = (Who: AppChatArchiveWho, Done: AppRawDone): IAppChatArchiveCall => {
        // ERROR-SUPPRESSED-JUSTIFIED: a thin delegate - `AppChatArchiveApi.Create` is guarded and always answers `Done`.
        return this.Api.Create(Who, null, Done, true);
    };

    public Write = (Who: AppChatArchiveWho, Value: AppChatSettingValue, Done: AppRawDone): IAppChatArchiveCall => {
        // ERROR-SUPPRESSED-JUSTIFIED: a thin delegate - `AppChatArchiveApi.Create` is guarded and always answers `Done`.
        return this.Api.Create(Who, Value, Done, true);
    };
}

export interface IAppChatSettingsScreen {
    Paint(Model: AppChatSettingsModel): void;
}

export class AppChatSettingsRun {
    public static readonly SlowConfirmMs: number = 15000;
    private Ask: AppChatArchiveAsk;
    private Port: IAppChatSettingsPort;
    private Screen: IAppChatSettingsScreen;
    private Clock: IAppLiveClock;
    private Settings: string[];
    private OnChanged: ((Setting: string) => void) | null;
    private Now: () => number;
    private Number: string;
    private ChatId: string;
    private Doc: AppChatStateDoc | null = null;
    private DocAt: number = 0;
    private Phase: string = AppChatSettingsModel.Loading;
    private Fail: string = "";
    private CanRetry: boolean = false;
    private ReadCall: IAppChatArchiveCall | null = null;
    private ReadVersion: number = 0;
    private ReadTimer: number = 0;
    private Retries: number = 0;
    private Rechecks: number = 0;
    private Confirms: number = 0;
    private Wanted: Map<string, AppChatSettingValue> = new Map<string, AppChatSettingValue>();
    private Calls: Map<string, IAppChatArchiveCall> = new Map<string, IAppChatArchiveCall>();
    private WriteVersion: Map<string, number> = new Map<string, number>();
    /** Setting -> the sentence to say if the change turns out not to have been applied. */
    private Awaiting: Map<string, string> = new Map<string, string>();
    private Notes: Map<string, string> = new Map<string, string>();
    private Tick: number = 0;
    private Elapsed: number = 0;
    private Stopped: boolean = false;

    public constructor(Ask: AppChatArchiveAsk, Port: IAppChatSettingsPort, Screen: IAppChatSettingsScreen, Clock: IAppLiveClock,
        Settings: string[], OnChanged: ((Setting: string) => void) | null = null, Now: () => number = Date.now) {
        this.Ask = Ask; this.Port = Port; this.Screen = Screen; this.Clock = Clock;
        this.Settings = Settings.slice(); this.OnChanged = OnChanged; this.Now = Now;
        const Who: AppChatArchiveWho = Ask();
        this.Number = Who.Number; this.ChatId = Who.ChatId;
    }

    /** Draw the loading rows and ask the service. */
    public Start = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Draw();
            this.Read();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.Start");
        }
    };

    /** "Try again" after the one sentence: back to loading, with a fresh set of tries. ⛔ Reads only. */
    public Retry = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || this.Phase !== AppChatSettingsModel.Failed) { return; }
            this.Phase = AppChatSettingsModel.Loading; this.Fail = ""; this.CanRetry = false; this.Retries = 0;
            this.Draw();
            this.Read();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.Retry");
        }
    };

    /**
     * The owner chose a value. True = the change was sent (and is shown already). False = nothing was sent: the setting
     * is saving or waiting, cannot be changed here, already has that value, or the value is not one the service admits.
     */
    public Set = (Setting: string, Value: AppChatSettingValue): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (this.MaySet(Setting, Value) === false) { return false; }
            const Who: AppChatArchiveWho = Object.assign(new AppChatArchiveWho(), this.Ask());
            if (this.Same(Who) === false) { this.Stop(); return false; }
            if (Who.Key.length === 0 || AppChatArchiveRules.Url(Who, false, true).length === 0) { return false; }
            Who.Setting = Setting;
            this.Wanted.set(Setting, Value);
            this.Notes.delete(Setting);
            this.AbortRead();
            const Version: number = (this.WriteVersion.get(Setting) || 0) + 1;
            this.WriteVersion.set(Setting, Version);
            const Call: IAppChatArchiveCall = this.Port.Write(Who, Value, new AppChatSettingsReply(this, Setting, Version).Heard);
            this.Calls.set(Setting, Call);
            this.Draw();
            Call.Send();
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.Set");
            return false;
        }
    };

    /** ⛔ ONE IN FLIGHT PER SETTING, only what the service says can be changed, only a real change. */
    private MaySet = (Setting: string, Value: AppChatSettingValue): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (this.Stopped === true || this.Doc === null || this.Phase !== AppChatSettingsModel.Ready) { return false; }
            if (this.Settings.indexOf(Setting) < 0 || this.Calls.has(Setting) === true || this.Awaiting.has(Setting) === true) { return false; }
            const Prop: AppChatProp = AppChatSettingsRules.PropOf(this.Doc, Setting);
            if (Prop.Can !== true || Prop.Pending === true) { return false; }
            if (AppChatSettingsRules.Allowed(Setting, Value) === false) { return false; }
            return AppChatSettingsRules.IsChange(Setting, Prop.Value, Value, this.NowMs());
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.MaySet");
            return false;
        }
    };

    private Read = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || this.ReadCall !== null) { return; }
            this.ReadTimer = this.Clear(this.ReadTimer);
            const Who: AppChatArchiveWho = Object.assign(new AppChatArchiveWho(), this.Ask());
            if (this.Same(Who) === false) { this.Stop(); return; }
            if (Who.Key.length === 0 || AppChatArchiveRules.Url(Who, true, true).length === 0) {
                this.FailWith(AppChatSettingsRules.SignInWhy, false);
                this.Draw();
                return;
            }
            this.ReadVersion = this.ReadVersion + 1;
            const Call: IAppChatArchiveCall = this.Port.Read(Who, new AppChatSettingsReply(this, "", this.ReadVersion).Heard);
            this.ReadCall = Call;
            Call.Send();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.Read");
        }
    };

    /** The dialog was opened for one chat of one subscriber: an answer for anybody else is never drawn. */
    private Same = (Who: AppChatArchiveWho): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return Who.ChatId === this.ChatId && Who.Number === this.Number;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.Same");
            return false;
        }
    };

    /** One answer, for the read (`Setting` "") or for a setting's write. ⛔ A late or repeated one is inert. */
    public Answer = (Setting: string, Version: number, Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true) { return; }
            if (Setting.length === 0) {
                if (Version !== this.ReadVersion || this.ReadCall === null) { return; }
                this.ReadCall = null;
                this.OnRead(AppChatSettingsRules.Verdict(Status, Text, this.ChatId));
                return;
            }
            if (Setting === AppChatSettingsRun.InfoAction) { this.OnInfo(Version, Status, Text); return; }
            if (Version !== this.WriteVersion.get(Setting) || this.Calls.has(Setting) === false) { return; }
            this.Calls.delete(Setting);
            this.OnWrite(Setting, AppChatSettingsRules.Verdict(Status, Text, this.ChatId));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.Answer");
        }
    };

    /**
     * ⭐ THE CONTACT'S DETAILS, ASKED ONCE PER OPENING, after the first read (1:1 chats only - a row list with "pin").
     * The service decides whether to put the question to WhatsApp (its cache, quiet period, spacing and daily cap); the
     * answer is the whole state document again, with `info` and/or `infoWhy`. ⛔ A read, never a change of anything.
     */
    public static readonly InfoAction: string = "info";
    private InfoAsked: boolean = false;
    private InfoVersion: number = 0;
    private InfoCall: IAppChatArchiveCall | null = null;
    private InfoNote: string = "";
    private AskInfo = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.InfoAsked === true || this.Stopped === true || this.Settings.indexOf(AppChatSettingsRules.Pin) < 0) { return; }
            const Who: AppChatArchiveWho = Object.assign(new AppChatArchiveWho(), this.Ask());
            if (this.Same(Who) === false || Who.Key.length === 0) { return; }
            this.InfoAsked = true; this.InfoVersion = this.InfoVersion + 1;
            Who.Setting = AppChatSettingsRun.InfoAction;
            this.InfoCall = this.Port.Write(Who, true, new AppChatSettingsReply(this, AppChatSettingsRun.InfoAction, this.InfoVersion).Heard);
            this.InfoCall.Send();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.AskInfo");
        }
    };
    private OnInfo = (Version: number, Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Version !== this.InfoVersion || this.InfoCall === null) { return; }
            this.InfoCall = null;
            const Got: AppChatSettingsVerdict = AppChatSettingsRules.Verdict(Status, Text, this.ChatId);
            if (Got.Doc.Ok === true) {
                this.InfoNote = Got.Doc.InfoWhy;
                if (this.Doc !== null && Got.Doc.Info === null) { this.Doc.InfoWhy = Got.Doc.InfoWhy; }
                if (this.Doc !== null && Got.Doc.Info !== null) { this.Doc.Info = Got.Doc.Info; }
            }
            this.Draw();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.OnInfo");
        }
    };

    private OnRead = (Got: AppChatSettingsVerdict): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Got.Doc.Ok === true) {
                if (this.Doc !== null && Got.Doc.Info === null && this.Doc.Info !== null) { Got.Doc.Info = this.Doc.Info; }
                this.Adopt(Got.Doc);
                this.AskInfo();
                this.Settle();
                this.Next();
                this.Draw();
                return;
            }
            if (Got.Lost === true || Got.RetrySec > 0) { this.Again(Got); this.Draw(); return; }
            // A final refusal: with nothing read yet it is the dialog's one sentence; else the last values stay.
            if (this.Doc === null) {
                let Why: string = Got.Why;
                if (Why.length === 0) { Why = AppChatSettingsRules.LostWhy; }
                this.FailWith(Why, Got.Why.length === 0);
            }
            this.Release();
            this.Draw();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.OnRead");
        }
    };

    private OnWrite = (Setting: string, Got: AppChatSettingsVerdict): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Got.Doc.Ok === true) {
                // The answer IS the state after the change: settled here unless WhatsApp has not confirmed it yet.
                this.Awaiting.set(Setting, AppChatSettingsRules.NotAppliedWhy);
                this.Adopt(Got.Doc);
                this.Settle();
                this.Next();
                this.Draw();
                return;
            }
            if (Got.Lost === true || Got.Pending === true) {
                let Why: string = AppChatSettingsRules.UnconfirmedWhy;
                if (Got.Pending === true && Got.Why.length > 0) { Why = Got.Why; }
                this.Awaiting.set(Setting, Why);
                this.Confirms = 0;
                this.Read();
                this.Draw();
                return;
            }
            // ⭐ REFUSED: the earlier value comes back, with the service's sentence beside the control.
            this.Wanted.delete(Setting);
            let Said: string = Got.Why;
            if (Said.length === 0) { Said = AppChatSettingsRules.RefusedWhy; }
            this.Notes.set(Setting, Said);
            this.Read();
            this.Draw();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.OnWrite");
        }
    };

    private Adopt = (Doc: AppChatStateDoc): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Doc = Doc; this.DocAt = this.Now();
            this.Phase = AppChatSettingsModel.Ready; this.Fail = ""; this.CanRetry = false; this.Retries = 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.Adopt");
        }
    };

    /** Every change that was waiting and that the service no longer calls pending: applied (told) or not (said). */
    private Settle = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Doc === null) { return; }
            for (const Setting of Array.from(this.Awaiting.keys())) {
                if (this.Calls.has(Setting) === true) { continue; }
                const Prop: AppChatProp = AppChatSettingsRules.PropOf(this.Doc, Setting);
                if (Prop.Pending === true) { continue; }
                const Wanted: AppChatSettingValue | undefined = this.Wanted.get(Setting);
                const Why: string = this.Awaiting.get(Setting) || "";
                this.Awaiting.delete(Setting);
                this.Wanted.delete(Setting);
                if (Wanted !== undefined && AppChatSettingsRules.Matches(Setting, Prop.Value, Wanted, this.NowMs()) === true) { this.Changed(Setting); continue; }
                this.Notes.set(Setting, Why);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.Settle");
        }
    };

    /** The waiting is over without an answer: the service's values come back, each change's sentence beside it. */
    private Release = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const Setting of Array.from(this.Awaiting.keys())) {
                if (this.Calls.has(Setting) === true) { continue; }
                this.Notes.set(Setting, this.Awaiting.get(Setting) || "");
                this.Awaiting.delete(Setting);
                this.Wanted.delete(Setting);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.Release");
        }
    };

    /** When the read is asked again after an ANSWER: a change awaiting WhatsApp, or the service still reading. */
    private Next = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Doc === null || this.Stopped === true || this.ReadTimer !== 0) { return; }
            if (this.AnyPending() === true) {
                if (this.Confirms >= AppChatSettingsRules.MaxConfirms * 2) { return; }
                this.Confirms = this.Confirms + 1;
                let Wait: number = AppChatSettingsRules.ConfirmMs;
                if (this.Confirms >= AppChatSettingsRules.MaxConfirms) { Wait = AppChatSettingsRun.SlowConfirmMs; }
                if (this.Confirms === AppChatSettingsRules.MaxConfirms) { this.Release(); }
                this.ReadTimer = this.Clock.Later(Wait, this.OnReadTimer);
                return;
            }
            this.Confirms = 0;
            if (this.Doc.Resolving !== true || this.Rechecks >= AppChatSettingsRules.MaxRechecks) { return; }
            this.Rechecks = this.Rechecks + 1;
            this.ReadTimer = this.Clock.Later(this.Doc.RecheckSec * 1000, this.OnReadTimer);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.Next");
        }
    };

    private AnyPending = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (this.Awaiting.size > 0) { return true; }
            if (this.Doc === null) { return false; }
            for (const Setting of this.Settings) {
                if (AppChatSettingsRules.PropOf(this.Doc, Setting).Pending === true) { return true; }
            }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.AnyPending");
            return false;
        }
    };

    /** A read that did not answer: asked again, quietly, a bounded number of times; then said. */
    private Again = (Got: AppChatSettingsVerdict): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Retries = this.Retries + 1;
            if (this.Retries > AppChatSettingsRules.MaxRetries) {
                let Why: string = Got.Why;
                if (Why.length === 0) { Why = AppChatSettingsRules.LostWhy; }
                if (this.Doc === null) { this.FailWith(Why, true); }
                this.Release();
                return;
            }
            let Wait: number = AppChatSettingsRules.RetryMs(this.Retries);
            if (Got.RetrySec > 0) { Wait = Got.RetrySec * 1000; }
            this.ReadTimer = this.Clock.Later(Wait, this.OnReadTimer);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.Again");
        }
    };

    private FailWith = (Why: string, CanRetry: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Phase = AppChatSettingsModel.Failed; this.Fail = Why; this.CanRetry = CanRetry;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.FailWith");
        }
    };

    private OnReadTimer = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.ReadTimer = 0;
            this.Read();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.OnReadTimer");
        }
    };

    // ERROR-SUPPRESSED-JUSTIFIED: kCallbackNotify.Call carries the guard and the handler.
    private Changed = (Setting: string): void => { kCallbackNotify.Call(this.OnChanged, Setting, "AppChatSettingsRun.Changed"); };

    /** The service's clock now (its `serverNowMs` plus the time since), 0 when it gave none. */
    private NowMs = (): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            if (this.Doc === null || this.Doc.ServerNowMs <= 0) { return 0; }
            return this.Doc.ServerNowMs + Math.max(0, this.Now() - this.DocAt);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.NowMs");
            return 0;
        }
    };

    /** What the dialog shows now. */
    public Model = (): AppChatSettingsModel => {
        const Out: AppChatSettingsModel = new AppChatSettingsModel();
        if (getCoreUtils().KErrorMode === true) { Out.Phase = AppChatSettingsModel.Failed; return Out; }
        try {
            Out.Phase = this.Phase; Out.Fail = this.Fail; Out.CanRetry = this.CanRetry; Out.Elapsed = this.Elapsed;
            if (this.Phase === AppChatSettingsModel.Failed) { return Out; }
            if (this.Doc !== null) { Out.Info = AppChatSettingsRules.InfoRows(this.Doc.Info); Out.InfoNote = this.InfoNote; Out.Locked = this.Doc.Locked; }
            for (const Setting of this.Settings) {
                if (this.Doc === null) { Out.Rows.push(AppChatSettingsRules.LoadingRow(Setting)); continue; }
                let Wanted: AppChatSettingValue | null = null;
                const Held: AppChatSettingValue | undefined = this.Wanted.get(Setting);
                if (Held !== undefined) { Wanted = Held; }
                Out.Rows.push(AppChatSettingsRules.RowOf(Setting, this.Doc, this.NowMs(), Wanted,
                    this.Awaiting.has(Setting) === true && this.Calls.has(Setting) === false, this.Notes.get(Setting) || ""));
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.Model");
            return Out;
        }
    };

    /** Is something in progress that the owner is waiting for (the first read, a save, a confirmation)? */
    private Working = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return this.Phase === AppChatSettingsModel.Loading || this.Calls.size > 0 || this.Awaiting.size > 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.Working");
            return false;
        }
    };

    private Draw = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true) { return; }
            if (this.Working() === false) { this.Elapsed = 0; this.Tick = this.Clear(this.Tick); }
            else if (this.Tick === 0) { this.Tick = this.Clock.Later(1000, this.OnTick); }
            this.Screen.Paint(this.Model());
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.Draw");
        }
    };

    /** Once a second while something is in progress: the quiet counter beside the hint moves. */
    private OnTick = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Tick = 0;
            if (this.Stopped === true || this.Working() === false) { return; }
            this.Elapsed = this.Elapsed + 1;
            this.Draw();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.OnTick");
        }
    };

    /** A change is about to be sent: a read still on its way would show the value from before it. */
    private AbortRead = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.ReadTimer = this.Clear(this.ReadTimer);
            if (this.ReadCall === null) { return; }
            const Call: IAppChatArchiveCall = this.ReadCall;
            this.ReadVersion = this.ReadVersion + 1;
            this.ReadCall = null;
            Call.Abort();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.AbortRead");
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: the dialog closing must stop timers and make late answers inert under a fault too. */
    public Stop = (): void => {
        try {
            this.Stopped = true;
            this.ReadVersion = this.ReadVersion + 1;
            this.Tick = this.Clear(this.Tick); this.ReadTimer = this.Clear(this.ReadTimer);
            if (this.ReadCall !== null) { const Call: IAppChatArchiveCall = this.ReadCall; this.ReadCall = null; Call.Abort(); }
            if (this.InfoCall !== null) { const Info: IAppChatArchiveCall = this.InfoCall; this.InfoCall = null; Info.Abort(); }
            // ⚠ A write in flight is NOT aborted: the owner chose it, and an abort would not take it back - it only loses the answer.
            this.Calls.clear();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.Stop");
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: cancelling an admitted timer is cleanup - it runs whatever the gate says. */
    private Clear = (Handle: number): number => {
        try {
            if (Handle !== 0) { this.Clock.Cancel(Handle); }
            return 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRun.Clear");
            return 0;
        }
    };
}

/** One request's answer, bound to the generation it was asked in - a class, not a closure over a changing dialog. */
export class AppChatSettingsReply {
    private Owner: AppChatSettingsRun;
    private Setting: string;
    private Version: number;

    public constructor(Owner: AppChatSettingsRun, Setting: string, Version: number) {
        this.Owner = Owner; this.Setting = Setting; this.Version = Version;
    }

    public Heard = (Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Owner.Answer(this.Setting, this.Version, Status, Text);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsReply.Heard");
        }
    };
}
